import type { SnapOption } from '@/types/database.types';
import { useState, useEffect } from 'react';
import { ImageIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import CashSnapWinnerBadge from './CashSnapWinnerBadge';

interface SnapOptionsProps {
  options: SnapOption[];
  totalVotes: number;
  userVote: string | null;
  onVote: (optionId: string) => void;
  onRemoveVote?: () => void;
  isSnapClosed: boolean;
  winningOptionId?: string | null;
  isCashSnap?: boolean;
}

export default function SnapOptions({
  options,
  totalVotes,
  userVote,
  onVote,
  onRemoveVote,
  isSnapClosed,
  winningOptionId,
  isCashSnap = false
}: SnapOptionsProps) {
  // Track image loading errors for each option
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [isVoting, setIsVoting] = useState(false);
  const [prevVoteCounts, setPrevVoteCounts] = useState<Record<string, number>>({});

  // Store previous vote counts for animation
  useEffect(() => {
    const newCounts: Record<string, number> = {};
    options.forEach(option => {
      newCounts[option.id] = option.vote_count;
    });
    setPrevVoteCounts(newCounts);
  }, [options]);

  const handleImageError = (optionId: string) => {
    setImageErrors(prev => ({
      ...prev,
      [optionId]: true
    }));
  };

  const handleVote = async (optionId: string) => {
    if (isVoting || isSnapClosed) return;
    
    // Don't allow voting on the same option again
    if (userVote === optionId) {
      toast.error('You have already voted for this option');
      return;
    }
    
    try {
      setIsVoting(true);
      await onVote(optionId);
    } finally {
      // Ensure isVoting is reset even if there's an error
      setTimeout(() => setIsVoting(false), 500);
    }
  };

  const handleRevokeVote = async () => {
    if (!onRemoveVote || isVoting || isSnapClosed || !userVote) return;
    
    try {
      setIsVoting(true);
      await onRemoveVote();
    } finally {
      setTimeout(() => setIsVoting(false), 500);
    }
  };

  return (
    <div className="grid gap-6 mb-8">
      {options.map((option) => {
        const votePercentage = totalVotes > 0 ? (option.vote_count / totalVotes) * 100 : 0;
        const isVoted = userVote === option.id;
        const isWinner = winningOptionId === option.id;
        
        // Check if we have a valid image URL and no loading error
        const hasValidImage = option.image_url && !imageErrors[option.id];
        
        // Calculate if the vote count has increased or decreased for animation
        const prevCount = prevVoteCounts[option.id] || 0;
        const countChanged = prevCount !== option.vote_count;
        const countIncreased = option.vote_count > prevCount;
        
        return (
          <motion.div
            key={option.id}
            className={`border rounded-lg p-4 transition-colors ${
              isVoted ? 'border-primary-500 bg-primary-50' : 'hover:border-primary-500'
            } ${isWinner && isCashSnap ? 'border-green-500 bg-green-50' : ''}`}
            initial={{ opacity: 0.9, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center">
                  <h3 className="font-medium text-gray-900">{option.title}</h3>
                  {isWinner && isCashSnap && (
                    <CashSnapWinnerBadge isWinner={true} className="ml-2" />
                  )}
                </div>
                <p className="text-gray-600 mt-1">{option.description}</p>
              </div>
              {hasValidImage ? (
                <div className="flex-shrink-0 ml-4">
                  <img
                    key={`${option.id}-${!imageErrors[option.id]}`}
                    src={option.image_url}
                    alt={option.title}
                    className="w-24 h-24 object-cover rounded-lg shadow-md"
                    onError={() => handleImageError(option.id)}
                    loading="lazy"
                  />
                </div>
              ) : option.image_url ? (
                <div className="flex-shrink-0 ml-4 w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                  <ImageIcon className="h-8 w-8 text-gray-400" />
                </div>
              ) : null}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {isVoted ? (
                    <div className="flex space-x-2">
                      <button
                        className="inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium transition-colors bg-primary-600 text-white border-transparent cursor-default"
                        disabled={true}
                      >
                        Your Vote
                      </button>
                      
                      {onRemoveVote && !isSnapClosed && (
                        <button
                          onClick={handleRevokeVote}
                          disabled={isVoting}
                          className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <XIcon className="h-4 w-4 mr-1" />
                          Revoke
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => handleVote(option.id)}
                      disabled={isSnapClosed || isVoting}
                      className="inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium transition-colors border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isVoting ? 'Voting...' : 'Vote'}
                    </button>
                  )}
                  <AnimatePresence mode="wait">
                    <motion.span 
                      key={`count-${option.vote_count}`}
                      initial={{ opacity: 0, y: countIncreased ? 10 : -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: countIncreased ? -10 : 10 }}
                      transition={{ duration: 0.3 }}
                      className="text-sm text-gray-600"
                    >
                      {option.vote_count} {option.vote_count === 1 ? 'vote' : 'votes'}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <AnimatePresence mode="wait">
                  <motion.span 
                    key={`percent-${votePercentage.toFixed(1)}`}
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 5 }}
                    transition={{ duration: 0.3 }}
                    className="text-sm font-medium text-gray-900"
                  >
                    {votePercentage.toFixed(1)}%
                  </motion.span>
                </AnimatePresence>
              </div>

              <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className={`absolute left-0 top-0 h-full ${
                    isWinner && isCashSnap 
                      ? 'bg-green-500' 
                      : isVoted 
                        ? 'bg-primary-500' 
                        : 'bg-primary-600'
                  }`}
                  initial={{ width: `${prevVoteCounts[option.id] ? (prevVoteCounts[option.id] / totalVotes) * 100 : 0}%` }}
                  animate={{ 
                    width: `${votePercentage}%`,
                    backgroundColor: isWinner && isCashSnap 
                      ? '#10B981' // green-500
                      : isVoted 
                        ? '#2563eb' // primary-500
                        : '#3b82f6' // primary-600
                  }}
                  transition={{ 
                    duration: 0.5,
                    ease: "easeInOut"
                  }}
                  key={`${option.id}-${option.vote_count}-${totalVotes}`} // Key changes when vote counts change
                />
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}